/**
 * GOAFREET MASTER SERVICES TAXONOMY & DATA MODEL
 * Canonical source of truth conforming to the Goafreet Services Architecture Guide.
 * 
 * Hierarchy:
 * Strategy -> Brand -> Creative -> Technology -> Content -> Media -> Growth -> Commerce -> Experience -> Production -> Analytics -> Automation
 */

export const serviceFamilies = [
  {
    id: "brand-strategy",
    slug: "brand-strategy",
    name: "Brand Strategy & Identity",
    shortDescription: "End-to-end brand positioning, visual identity systems, and brand architecture.",
    icon: "🎨",
    color: "#E91E63",
    capabilities: [
      "Brand positioning & architecture",
      "Logo design & visual identity systems",
      "Brand guidelines & typography systems",
      "Corporate stationery & pitch deck design",
      "Rebranding & brand perception audits",
      "Employer & executive branding",
    ],
    services: [
      { name: "Brand Strategy & Identity", desc: "Complete brand positioning & voice", path: "/services/brand-creative/branding/", status: "active", icon: "🎨", color: "#E91E63" },
      { name: "Logo Design & Identity", desc: "Memorable logo systems & visual assets", path: "/services/brand-creative/logo-designing/", status: "active", icon: "✨", color: "#9C27B0" },
      { name: "Corporate Collateral & Decks", desc: "Pitch decks, profiles & business stationery", path: "/services/brand-creative/branding/", status: "active", icon: "📐", color: "#EC4899" },
    ],
  },
  {
    id: "creative-design",
    slug: "creative-design",
    name: "Creative & Design",
    shortDescription: "High-impact campaign visuals, digital banners, marketing collateral, and creative direction.",
    icon: "🎭",
    color: "#FF5722",
    capabilities: [
      "Campaign key visual development",
      "Social media creative & digital banner design",
      "Infographics & data visualization",
      "Marketing templates & multi-format adaptations",
      "Promotional & festive campaign design",
    ],
    services: [
      { name: "Graphics & Creative Design", desc: "Visual storytelling & campaign assets", path: "/services/brand-creative/graphics-designing/", status: "active", icon: "🎭", color: "#FF5722" },
      { name: "Campaign Key Visuals", desc: "High-converting advertising creative sets", path: "/services/brand-creative/graphics-designing/", status: "active", icon: "🖼️", color: "#F97316" },
      { name: "Presentation & Deck Design", desc: "Investor decks & corporate reports", path: "/contact-support/", status: "planned", icon: "📊", color: "#FB923C" },
    ],
  },
  {
    id: "ui-ux",
    slug: "ui-ux",
    name: "UI/UX & Product Design",
    shortDescription: "User-centric interface design, design systems, and conversion-focused customer journeys.",
    icon: "🖌️",
    color: "#673AB7",
    capabilities: [
      "User research, personas & journey mapping",
      "Wireframing & interactive prototyping",
      "Design systems & scalable component libraries",
      "SaaS product, portal & dashboard UX",
      "Conversion rate & checkout UX optimization",
    ],
    services: [
      { name: "UI/UX Designing", desc: "Human-centric web & product interface design", path: "/services/brand-creative/ui-ux-designing/", status: "active", icon: "🖌️", color: "#673AB7" },
      { name: "Design Systems & Prototyping", desc: "Interactive prototypes & UI components", path: "/services/brand-creative/ui-ux-designing/", status: "active", icon: "📐", color: "#8B5CF6" },
      { name: "Conversion & Checkout UX", desc: "Frictionless funnels & CRO-oriented design", path: "/services/brand-creative/ui-ux-designing/", status: "active", icon: "⚡", color: "#A855F7" },
    ],
  },
  {
    id: "development",
    slug: "development",
    name: "Web, App & Software Development",
    shortDescription: "Custom web applications, mobile apps, enterprise portals, and scalable cloud engineering.",
    icon: "💻",
    color: "#2196F3",
    capabilities: [
      "Custom website & enterprise portal development",
      "Ecommerce & headless commerce architecture",
      "Mobile app development (iOS, Android, Flutter, React Native)",
      "Desktop software & internal workflow applications",
      "Website maintenance, speed optimization & security",
      "API development & third-party integrations",
    ],
    services: [
      { name: "Website Development", desc: "High-performance websites & web platforms", path: "/services/software-engineering/website-development/", status: "active", icon: "💻", color: "#2196F3" },
      { name: "Ecommerce Development", desc: "Custom storefronts, Shopify Plus & headless", path: "/services/ecommerce-marketplace/ecommerce-development/", status: "active", icon: "🛒", color: "#F97316" },
      { name: "Mobile App Development", desc: "Native & cross-platform iOS & Android apps", path: "/services/software-engineering/mobile-app-development/", status: "active", icon: "📱", color: "#4CAF50" },
      { name: "Desktop Software Development", desc: "Robust business process & desktop software", path: "/services/software-engineering/desktop-app-development/", status: "active", icon: "🖥️", color: "#FF9800" },
      { name: "Website Maintenance & Speed", desc: "SLA support, security & Core Web Vitals", path: "/services/software-engineering/website-maintenance/", status: "active", icon: "🛠️", color: "#00BCD4" },
    ],
  },
  {
    id: "seo-discovery",
    slug: "seo-discovery",
    name: "Search, SEO & AI Discovery",
    shortDescription: "Technical SEO, organic search dominance, entity optimization, and Generative Engine Optimization.",
    icon: "🔍",
    color: "#10B981",
    capabilities: [
      "Technical SEO & Core Web Vitals optimization",
      "On-page entity optimization & semantic content",
      "Topic clusters, pillar pages & content strategy",
      "Local SEO & Google Business Profile dominance",
      "Generative Engine Optimization (GEO) & AI overview visibility",
      "Authority building & digital PR link acquisition",
    ],
    services: [
      { name: "Search Engine Optimization (SEO)", desc: "Comprehensive organic ranking & revenue growth", path: "/services/digital-marketing/search-engine-optimization/", status: "active", icon: "🔍", color: "#10B981" },
      { name: "Technical SEO & Speed", desc: "Crawlability, structured data & performance", path: "/services/digital-marketing/search-engine-optimization/", status: "active", icon: "⚙️", color: "#059669" },
      { name: "Generative Engine Optimization (GEO)", desc: "Optimize visibility for AI search & answer engines", path: "/services/digital-marketing/search-engine-optimization/", status: "active", icon: "🤖", color: "#34D399" },
    ],
  },
  {
    id: "performance-media",
    slug: "performance-media",
    name: "Performance & Paid Media",
    shortDescription: "ROI-driven customer acquisition across Google, Meta, programmatic, and paid networks.",
    icon: "📊",
    color: "#F59E0B",
    capabilities: [
      "Paid search, shopping & Performance Max (Google Ads)",
      "Paid social advertising (Meta, LinkedIn, YouTube, X)",
      "Full-funnel acquisition, retargeting & remarketing",
      "Conversion tracking, GA4 & server-side Conversion API",
      "ROAS optimization, A/B testing & creative iteration",
    ],
    services: [
      { name: "Pay Per Click (PPC) Management", desc: "High-intent Google Ads & search campaigns", path: "/services/digital-marketing/pay-per-click/", status: "active", icon: "📊", color: "#F59E0B" },
      { name: "Meta Advertising", desc: "Facebook & Instagram full-funnel ad campaigns", path: "/services/digital-marketing/facebook-marketing/", status: "active", icon: "🎯", color: "#1877F2" },
      { name: "Performance Marketing Strategy", desc: "Targeted CAC reduction & ROAS scaling", path: "/services/digital-marketing/pay-per-click/", status: "active", icon: "📈", color: "#D97706" },
    ],
  },
  {
    id: "social-content",
    slug: "social-content",
    name: "Social Media & Content",
    shortDescription: "Audience growth, brand community management, and multi-channel creative storytelling.",
    icon: "📣",
    color: "#2563EB",
    capabilities: [
      "Omni-channel social strategy & monthly editorial calendars",
      "Platform-specific content: Instagram, LinkedIn, YouTube, Facebook, X",
      "B2B thought leadership & founder personal branding",
      "Community management, social listening & engagement",
      "Long-form content, technical copywriting & whitepapers",
    ],
    services: [
      { name: "Social Media Marketing", desc: "Omni-channel brand growth & campaign strategy", path: "/services/digital-marketing/social-media-marketing/", status: "active", icon: "📣", color: "#2563EB" },
      { name: "Instagram Marketing", desc: "Reels production, stories & viral audience growth", path: "/services/digital-marketing/instagram-marketing/", status: "active", icon: "📸", color: "#E1306C" },
      { name: "Facebook Marketing", desc: "Meta ad funnels, community building & reach", path: "/services/digital-marketing/facebook-marketing/", status: "active", icon: "👥", color: "#1877F2" },
      { name: "LinkedIn B2B Marketing", desc: "Corporate branding & B2B decision-maker outreach", path: "/services/digital-marketing/linkedIn-marketing/", status: "active", icon: "💼", color: "#0A66C2" },
      { name: "YouTube Marketing", desc: "Channel management, video SEO & audience retention", path: "/services/digital-marketing/youtube-marketing/", status: "active", icon: "📹", color: "#FF0000" },
      { name: "Twitter / X Marketing", desc: "Real-time trends, PR & brand dialogue", path: "/services/digital-marketing/twitter-marketing/", status: "active", icon: "🐦", color: "#1DA1F2" },
    ],
  },
  {
    id: "video-production",
    slug: "video-production",
    name: "Video, Photography & Production",
    shortDescription: "Corporate films, commercial DVCs, product photography, 3D CGI animation, and podcasting.",
    icon: "🎬",
    color: "#DC2626",
    capabilities: [
      "Corporate films, brand documentaries & leadership interviews",
      "Commercial video advertisements (TVC & DVC production)",
      "High-end ecommerce & industrial product photography",
      "3D product visualization, CGI animation & motion graphics",
      "Corporate podcast production & audio editing",
    ],
    services: [
      { name: "Corporate & Brand Films", desc: "Cinematic company profiles, culture & story films", path: "/services/production-offline/video-production/", status: "active", icon: "🎬", color: "#DC2626" },
      { name: "Commercial & DVC Production", desc: "High-impact digital commercials for social & ads", path: "/services/production-offline/video-production/", status: "active", icon: "🎥", color: "#B91C1C" },
      { name: "Product Photography & 360°", desc: "Studio & lifestyle photography for ecommerce & catalogs", path: "/contact-support/", status: "planned", icon: "📸", color: "#EF4444" },
      { name: "3D Animation & Motion Graphics", desc: "CGI product demos, technical animation & VFX", path: "/services/production-offline/video-production/", status: "active", icon: "✨", color: "#F87171" },
    ],
  },
  {
    id: "influencer-pr",
    slug: "influencer-pr",
    name: "Influencer, PR & Communications",
    shortDescription: "Creator partnerships, digital public relations, media pitching, and reputation management.",
    icon: "⭐",
    color: "#8B5CF6",
    capabilities: [
      "Creator identification, vetting & fraud detection",
      "Macro, micro, nano & B2B influencer campaign management",
      "Digital PR, press releases & editorial publication outreach",
      "Online Reputation Management (ORM) & review monitoring",
      "Executive communications & crisis response strategy",
    ],
    services: [
      { name: "Online Reputation Management", desc: "Proactive review management & brand protection", path: "/services/production-offline/online-reputation-management/", status: "active", icon: "⭐", color: "#8B5CF6" },
      { name: "Influencer & Creator Marketing", desc: "Targeted creator collaborations & seeding campaigns", path: "/services/production-offline/influencer-marketing/", status: "active", icon: "🌟", color: "#A78BFA" },
      { name: "Digital PR & Media Relations", desc: "Press release syndication & Tier-1 publication features", path: "/contact-support/", status: "planned", icon: "📰", color: "#7C3AED" },
    ],
  },
  {
    id: "ecommerce-marketplace",
    slug: "ecommerce-marketplace",
    name: "Ecommerce & Marketplace Growth",
    shortDescription: "End-to-end seller operations, catalog optimization, advertising, and scaling on major marketplaces.",
    icon: "🛒",
    color: "#F97316",
    capabilities: [
      "Amazon seller & vendor management, A+ content, brand store",
      "Flipkart catalog management, PLA/PCA ads & account health",
      "Meesho cataloging, pricing strategy & promotions",
      "D2C store optimization, checkout conversion & retention",
      "Multi-channel inventory, return analysis & buy box protection",
    ],
    services: [
      { name: "Amazon Account Management", desc: "Full-cycle Amazon seller growth, listings & FBA", path: "/services/ecommerce-marketplace/amazon-account-management-services/", status: "active", icon: "📦", color: "#FF9900" },
      { name: "Flipkart Account Management", desc: "Cataloging, listing SEO & compliance on Flipkart", path: "/services/ecommerce-marketplace/flipkart-account-management-services/", status: "active", icon: "🛒", color: "#2874F0" },
      { name: "Flipkart Marketing & Ads", desc: "Sponsored products, PCA campaigns & ROI management", path: "/services/ecommerce-marketplace/flipkart-marketing/", status: "active", icon: "🏷️", color: "#0284C7" },
      { name: "Meesho Account Management", desc: "Catalog scale, price parity & reseller distribution", path: "/services/ecommerce-marketplace/meesho-account-management-services/", status: "active", icon: "👜", color: "#F43397" },
      { name: "Meesho Ecommerce Marketing", desc: "Order acceleration & category promotion campaigns", path: "/services/ecommerce-marketplace/mesho-ecommerce-marketing/", status: "active", icon: "🛍️", color: "#EC4899" },
    ],
  },
  {
    id: "offline-ooh",
    slug: "offline-ooh",
    name: "Offline, OOH, Events & Activation",
    shortDescription: "Transit advertising, billboards, and experiential brand activations.",
    icon: "🛺",
    color: "#D97706",
    capabilities: [
      "Transit advertising: auto rickshaw branding, bus & metro wraps",
      "Out-of-Home (OOH) billboards, unipolar displays & DOOH screens",
      // "Exhibition booth design, stall fabrication & trade show branding",
      "Retail experiential activations, product sampling & roadshows",
    ],
    services: [
      { name: "Transit & Rickshaw Branding", desc: "High-density city transit banner marketing", path: "/services/production-offline/auto-riksha-banner-marketing/", status: "active", icon: "🛺", color: "#D97706" },
      { name: "OOH & Billboard Advertising", desc: "Strategic hoardings, gantries & DOOH digital screens", path: "/contact-support/", status: "planned", icon: "🏙️", color: "#B45309" },
      // { name: "Exhibitions & Trade Show Stalls", desc: "Booth design, fabrication & visitor engagement", path: "/contact-support/", status: "planned", icon: "🎪", color: "#F59E0B" },
    ],
  },
  {
    id: "print-packaging",
    slug: "print-packaging",
    name: "Print, Packaging & Merchandise",
    shortDescription: "Packaging design, structural dielines, commercial print production, and corporate merchandise.",
    icon: "📦",
    color: "#059669",
    capabilities: [
      "Consumer product packaging design (FMCG, cosmetics, retail)",
      "Structural packaging dielines, 3D mockups & print-ready artwork",
      "Commercial & large format print production (brochures, stands)",
      "Corporate welcome kits, custom merchandise & employee gifting",
    ],
    services: [
      { name: "Product Packaging Design", desc: "Label design, structural dielines & 3D mockups", path: "/services/brand-creative/packaging-design/", status: "active", icon: "📦", color: "#059669" },
      { name: "Commercial Print Production", desc: "Premium brochures, annual reports & POS collateral", path: "/contact-support/", status: "planned", icon: "🖨️", color: "#047857" },
      { name: "Corporate Merchandise & Kits", desc: "Branded apparel, client gift boxes & welcome kits", path: "/contact-support/", status: "planned", icon: "🎁", color: "#10B981" },
    ],
  },
  {
    id: "crm-automation",
    slug: "crm-automation",
    name: "CRM, Automation & Lead Generation",
    shortDescription: "B2B lead generation, CRM pipelines, WhatsApp API automation, and lifecycle email nurturing.",
    icon: "⚡",
    color: "#0284C7",
    capabilities: [
      "B2B lead generation & account-based marketing (ABM)",
      "CRM implementation, pipeline automation & lead scoring",
      "WhatsApp Business API integration & automated broadcast sequences",
      "Lifecycle email marketing, drip sequences & abandoned cart flows",
    ],
    services: [
      { name: "B2B Lead Generation", desc: "Targeted prospect outreach & qualified pipeline growth", path: "/services/digital-marketing/lead-generation/", status: "active", icon: "🎯", color: "#0284C7" },
      { name: "CRM & Marketing Automation", desc: "Lead routing, scoring & automated customer journeys", path: "/services/ai-automation/crm-and-marketing-automation/", status: "active", icon: "⚙️", color: "#0369A1" },
      { name: "Email & WhatsApp Marketing", desc: "Drip nurture sequences & transactional automation", path: "/services/ai-automation/crm-and-marketing-automation/", status: "active", icon: "💬", color: "#38BDF8" },
    ],
  },
  {
    id: "analytics-growth",
    slug: "analytics-growth",
    name: "Analytics, Research & Growth Consulting",
    shortDescription: "Conversion rate optimization, data dashboards, market research, and international expansion.",
    icon: "📈",
    color: "#4F46E5",
    capabilities: [
      "Marketing analytics, GA4 server-side tracking & attribution",
      "Conversion Rate Optimization (CRO) & funnel experiments",
      "Market opportunity research, sizing & competitor benchmarking",
      "Go-to-market planning & global expansion consulting",
    ],
    services: [
      { name: "Marketing Analytics & Attribution", desc: "GA4, conversion tracking & unified ROI dashboards", path: "/contact-support/", status: "planned", icon: "📊", color: "#4F46E5" },
      { name: "Conversion Rate Optimization (CRO)", desc: "Funnel audits, landing page testing & UX gains", path: "/contact-support/", status: "planned", icon: "⚡", color: "#4338CA" },
      { name: "Market Research & GTM Strategy", desc: "Competitor intelligence & new market expansion", path: "/contact-support/", status: "planned", icon: "🔬", color: "#6366F1" },
    ],
  },
];

/**
 * Top 5 flagship highlights for the Mega Menu sidebar
 */
export const servicesHighlights = [
  { name: "Website Development", desc: "Need a high-converting website? Custom modern web development & apps", path: "/services/software-engineering/website-development/", icon: "💻", color: "#2196F3" },
  { name: "Mobile App Development", desc: "Launching iOS/Android products? Native mobile engineering & UX", path: "/services/software-engineering/mobile-app-development/", icon: "📱", color: "#4CAF50" },
  { name: "Search Engine Optimization", desc: "Want more Google leads? Enterprise SEO, AI search & technical audits", path: "/services/digital-marketing/search-engine-optimization/", icon: "🔍", color: "#10B981" },
  { name: "Brand Strategy & Identity", desc: "Starting or scaling a brand? Iconic logos & complete visual systems", path: "/services/brand-creative/branding/", icon: "🎨", color: "#E91E63" },
  { name: "Amazon Account Management", desc: "Struggling on Amazon? End-to-end sales growth & PPC optimization", path: "/services/ecommerce-marketplace/amazon-account-management-services/", icon: "📦", color: "#FF9900" },
];

/**
 * Pre-formatted mega menu sections for the desktop Visme navigation (Rule 4)
 * Organized into 4 strategic discipline clusters:
 * Group 1: Brand Strategy, Creative & Design, UI/UX, Web & App Development
 * Group 2: Search/SEO & AI, Performance Media, Social Media, CRM & Automation
 * Group 3: Video Production, Influencer & PR, Offline & OOH, Print & Packaging
 * Group 4: Ecommerce & Marketplaces, Analytics & Growth Consulting
 */
export const megaMenuSections = [
  {
    title: "Brand, Design & Technology",
    items: [
      { name: "Brand Strategy & Identity", desc: "Carve unique market positioning & premium perception", path: "/services/brand-creative/branding/", icon: "🎨", color: "#E91E63" },
      { name: "Logo Design & Visual Assets", desc: "Memorable logo marks, typography & brand guidelines", path: "/services/brand-creative/logo-designing/", icon: "✨", color: "#9C27B0" },
      { name: "UI/UX & Product Design", desc: "Frictionless user flows that maximize conversion rates", path: "/services/brand-creative/ui-ux-designing/", icon: "🖌️", color: "#673AB7" },
      { name: "Website Development", desc: "High-speed, SEO-architected web platforms & portals", path: "/services/software-engineering/website-development/", icon: "💻", color: "#2196F3" },
      { name: "Mobile App Development", desc: "Scalable native & cross-platform iOS and Android apps", path: "/services/software-engineering/mobile-app-development/", icon: "📱", color: "#4CAF50" },
      { name: "Desktop Applications", desc: "Robust cross-platform software for enterprise operations", path: "/services/software-engineering/desktop-app-development/", icon: "🖥️", color: "#FF9800" },
      { name: "Website Maintenance", desc: "SLA-backed uptime, security monitoring & speed tuning", path: "/services/software-engineering/website-maintenance/", icon: "🛠️", color: "#00BCD4" },
    ],
  },
  {
    title: "Search, Media & Social Growth",
    items: [
      { name: "Search Engine Optimization (SEO)", desc: "Dominate Google Page 1 for high-intent buyer searches", path: "/services/digital-marketing/search-engine-optimization/", icon: "🔍", color: "#10B981" },
      { name: "Performance Marketing (PPC)", desc: "Profitable Google Ads funnels focused on ROAS & CAC", path: "/services/digital-marketing/pay-per-click/", icon: "📊", color: "#F59E0B" },
      { name: "Social Media Marketing", desc: "Build an engaged community that fuels organic demand", path: "/services/digital-marketing/social-media-marketing/", icon: "📣", color: "#2563EB" },
      { name: "Instagram Marketing", desc: "High-retention Reels, visual storytelling & viral reach", path: "/services/digital-marketing/instagram-marketing/", icon: "📸", color: "#E1306C" },
      { name: "Facebook Advertising", desc: "Targeted Meta ad funnels engineered for scale", path: "/services/digital-marketing/facebook-marketing/", icon: "👥", color: "#1877F2" },
      { name: "LinkedIn B2B Marketing", desc: "Direct executive positioning & enterprise lead pipelines", path: "/services/digital-marketing/linkedIn-marketing/", icon: "💼", color: "#0A66C2" },
      { name: "YouTube Marketing", desc: "Long-term video authority, search rankings & monetization", path: "/services/digital-marketing/youtube-marketing/", icon: "📹", color: "#FF0000" },
      { name: "Twitter / X Marketing", desc: "Founder-led thought leadership & industry conversations", path: "/services/digital-marketing/twitter-marketing/", icon: "🐦", color: "#1DA1F2" },
    ],
  },
  {
    title: "Production, PR & Offline Media",
    items: [
      { name: "Online Reputation Management", desc: "Protect brand credibility, resolve reviews & build trust", path: "/services/production-offline/online-reputation-management/", icon: "⭐", color: "#8B5CF6" },
      { name: "Transit Advertising (Auto Rickshaw)", desc: "High-density hyperlocal mobile billboards in Tier 1-2 cities", path: "/services/production-offline/auto-riksha-banner-marketing/", icon: "🛺", color: "#D97706" },
      { name: "Corporate Video & Films", desc: "Commercial-grade brand films, product demos & DVCs", path: "/services/production-offline/video-production/", icon: "🎬", color: "#DC2626" },
      { name: "Influencer & Creator Marketing", desc: "Vetted creator collaborations with verifiable ROI", path: "/services/production-offline/influencer-marketing/", icon: "🌟", color: "#A78BFA" },
      { name: "Packaging Design & Print", desc: "Retail-ready packaging dielines that command shelf attention", path: "/services/brand-creative/packaging-design/", icon: "📦", color: "#059669" },
      // { name: "Exhibition & Event Branding", desc: "Trade show stall design & collateral that captures attention", path: "/contact-support/", icon: "🎪", color: "#F59E0B" },
    ],
  },
  {
    title: "Ecommerce & Marketplace Growth",
    items: [
      { name: "Ecommerce Development", desc: "Custom storefronts, Shopify Plus & headless architectures", path: "/services/ecommerce-marketplace/ecommerce-development/", icon: "🛒", color: "#F97316" },
      { name: "Amazon Account Management", desc: "Full-service cataloging, buy-box defense & FBA operations", path: "/services/ecommerce-marketplace/amazon-account-management-services/", icon: "📦", color: "#FF9900" },
      { name: "Flipkart Account Management", desc: "Listing optimization, F-Assured tiering & sales growth", path: "/services/ecommerce-marketplace/flipkart-account-management-services/", icon: "🛒", color: "#2874F0" },
      { name: "Flipkart Marketing & Ads", desc: "High-converting PLA & PCA advertising campaigns", path: "/services/ecommerce-marketplace/flipkart-marketing/", icon: "🏷️", color: "#0284C7" },
      { name: "Meesho Account Management", desc: "Zero-commission catalog expansion & pricing efficiency", path: "/services/ecommerce-marketplace/meesho-account-management-services/", icon: "👜", color: "#F43397" },
      { name: "Meesho Ecommerce Growth", desc: "Volume acceleration with RTO-loss risk mitigation", path: "/services/ecommerce-marketplace/mesho-ecommerce-marketing/", icon: "🛍️", color: "#EC4899" },
      { name: "CRM & Marketing Automation", desc: "Automated WhatsApp API & lead nurturing workflows", path: "/services/ai-automation/crm-and-marketing-automation/", icon: "⚡", color: "#0284C7" },
      { name: "B2B Lead Generation", desc: "Outbound qualified buyer pipelines with verified contacts", path: "/services/digital-marketing/lead-generation/", icon: "🎯", color: "#0369A1" },
      { name: "Analytics & Growth Consulting", desc: "Unit economics modeling, CRO audits & GTM execution", path: "/contact-support/", icon: "📈", color: "#4F46E5" },
    ],
  },
];
